// Enhanced file management methods for DocumentAccelerationApp
// This extends the main app with advanced file management features

import { VersionComparator } from './fileManager.js';

// Export a function that extends the DocumentAccelerationApp class
export function extendDocumentAccelerationApp(DocumentAccelerationApp) {

// File management methods removed - search and filtering functionality removed

// Create new file modal
DocumentAccelerationApp.prototype.showCreateFileModal = function() {
    const modal = document.createElement('div');
    modal.className = 'modal create-file-modal';
    modal.style.display = 'flex';
    
    modal.innerHTML = `
        <div class="modal-content">
            <div class="modal-header">
                <h3>Create New File</h3>
                <button class="close-btn">&times;</button>
            </div>
            <div class="modal-body">
                <div class="form-group">
                    <label for="new-file-name">File Name *</label>
                    <input type="text" id="new-file-name" placeholder="document.md" required>
                </div>
                
                <div class="file-template-selector">
                    <label>Choose Template:</label>
                    <div class="template-option selected" data-template="blank">
                        <strong>Blank Document</strong>
                        <p>Start with an empty markdown file</p>
                    </div>
                    <div class="template-option" data-template="aws-narrative">
                        <strong>AWS Narrative Template</strong>
                        <p>Pre-structured template for AWS-style documents</p>
                    </div>
                    <div class="template-option" data-template="working-backwards">
                        <strong>Working Backwards Template</strong>
                        <p>Amazon-style working backwards document structure</p>
                    </div>
                    <div class="template-option" data-template="faq">
                        <strong>FAQ Template</strong>
                        <p>Frequently asked questions document structure</p>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary cancel-btn">Cancel</button>
                    <button type="button" class="btn btn-primary create-btn">Create File</button>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    // Template selection
    modal.querySelectorAll('.template-option').forEach(option => {
        option.addEventListener('click', () => {
            modal.querySelectorAll('.template-option').forEach(opt => opt.classList.remove('selected'));
            option.classList.add('selected');
        });
    });

    // Modal controls
    modal.querySelector('.close-btn').addEventListener('click', () => {
        document.body.removeChild(modal);
    });

    modal.querySelector('.cancel-btn').addEventListener('click', () => {
        document.body.removeChild(modal);
    });

    modal.querySelector('.create-btn').addEventListener('click', async () => {
        await this.createNewFile(modal);
    });

    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            document.body.removeChild(modal);
        }
    });

    // Focus file name input
    modal.querySelector('#new-file-name').focus();
};

DocumentAccelerationApp.prototype.createNewFile = async function(modal) {
    const fileName = modal.querySelector('#new-file-name').value.trim();
    const selectedTemplate = modal.querySelector('.template-option.selected').dataset.template;

    if (!fileName) {
        alert('Please enter a file name');
        return;
    }

    // Ensure .md extension
    const finalFileName = fileName.endsWith('.md') ? fileName : fileName + '.md';

    try {
        const content = this.getTemplateContent(selectedTemplate);
        await this.fileManager.createFile(finalFileName, content, 'user');
        
        document.body.removeChild(modal);
        await this.loadProjectFiles();
        
        // Open the new file
        this.openFile(finalFileName);
        
    } catch (error) {
        console.error('Error creating file:', error);
        alert('Failed to create file. Please try again.');
    }
};

DocumentAccelerationApp.prototype.getTemplateContent = function(template) {
    const templates = {
        blank: '',
        'aws-narrative': `# Document Title

## Executive Summary

## Problem Statement

## Solution Overview

## Architecture

## Implementation Details

## Security Considerations

## Performance and Scalability

## Cost Analysis

## Risk Assessment

## Conclusion and Next Steps
`,
        'working-backwards': `# Press Release

**FOR IMMEDIATE RELEASE**

## Product Announcement

## Customer Benefits

## Key Features

## Availability

---

# FAQ

## General Questions

## Technical Questions

## Business Questions

---

# Customer Experience

## User Journey

## Success Scenarios

---

# Technical Requirements

## System Architecture

## Implementation Plan
`,
        'faq': `# Frequently Asked Questions

## General Questions

### Q: [Question]
**A:** [Answer]

## Technical Questions

### Q: [Question]
**A:** [Answer]

## Implementation Questions

### Q: [Question]
**A:** [Answer]

## Business Questions

### Q: [Question]
**A:** [Answer]
`
    };

    return templates[template] || '';
};

// Version comparison
DocumentAccelerationApp.prototype.performVersionComparison = async function(modal, filename, version1, version2) {
    try {
        const comparison = await this.fileManager.compareVersions(filename, version1, version2);
        const summary = VersionComparator.getChangesSummary(comparison.diff);
        
        // Show changes summary
        const summaryDiv = modal.querySelector('#changes-summary');
        summaryDiv.innerHTML = `
            <div class="change-stat added">+${summary.added} added</div>
            <div class="change-stat removed">-${summary.removed} removed</div>
            <div class="change-stat changed">~${summary.changed} changed</div>
            <div class="change-stat unchanged">${summary.unchanged} unchanged</div>
        `;
        summaryDiv.style.display = 'flex';

        // Show diff
        const diffViewer = modal.querySelector('#diff-viewer');
        diffViewer.innerHTML = VersionComparator.renderDiff(comparison.diff);
        diffViewer.style.display = 'block';

    } catch (error) {
        console.error('Error performing version comparison:', error);
        alert('Failed to compare versions.');
    }
};

// Enhanced file watching
DocumentAccelerationApp.prototype.setupFileWatching = function() {
    if (this.currentOpenFile) {
        this.fileManager.watchFile(this.currentOpenFile, (update) => {
            if (update.createdBy === 'agent') {
                this.showFileUpdateNotification(update);
            }
        });
    }
};

DocumentAccelerationApp.prototype.showFileUpdateNotification = function(update) {
    const notification = document.createElement('div');
    notification.className = 'file-update-notification';
    notification.innerHTML = `
        <div class="notification-content">
            <strong>File Updated by AI</strong>
            <p>${update.filename} has been modified by the assistant</p>
            <div class="notification-actions">
                <button class="btn btn-secondary" onclick="this.dismissNotification(this.parentElement.parentElement.parentElement)">Dismiss</button>
                <button class="btn btn-primary" onclick="this.reloadCurrentFile()">Reload</button>
            </div>
        </div>
    `;

    document.body.appendChild(notification);

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
        if (notification.parentElement) {
            document.body.removeChild(notification);
        }
    }, 10000);
};

DocumentAccelerationApp.prototype.dismissNotification = function(notification) {
    if (notification.parentElement) {
        document.body.removeChild(notification);
    }
};

DocumentAccelerationApp.prototype.reloadCurrentFile = async function() {
    if (this.currentOpenFile) {
        await this.openFile(this.currentOpenFile);
    }
};

// Cleanup on page unload
DocumentAccelerationApp.prototype.cleanup = function() {
    if (this.fileManager) {
        this.fileManager.destroy();
    }
};

// Add cleanup listener
window.addEventListener('beforeunload', () => {
    if (window.app && window.app.cleanup) {
        window.app.cleanup();
    }
});

// Project PDF export
DocumentAccelerationApp.prototype.exportProjectToPDF = function() {
    alert('PDF export is not yet implemented. Please use the markdown content directly or copy the text for now.');
};

// PDF export progress function removed - not implemented
DocumentAccelerationApp.prototype.showPDFExportProgress = function(steps) {
    // PDF export not implemented
    return;
    progressModal.innerHTML = `
        <h3>Generating PDF</h3>
        <p>Please wait while we prepare your document...</p>
        <div class="progress-bar">
            <div class="progress-fill" style="width: 0%"></div>
        </div>
        <div class="progress-steps">
            ${steps.map(step => `<div class="progress-step">${step}</div>`).join('')}
        </div>
    `;

    document.body.appendChild(progressModal);
    return progressModal;
};

DocumentAccelerationApp.prototype.updatePDFProgress = function(progressModal, currentStep, progress) {
    const progressFill = progressModal.querySelector('.progress-fill');
    const steps = progressModal.querySelectorAll('.progress-step');
    
    // Update progress bar
    progressFill.style.width = `${progress}%`;
    
    // Update step states
    steps.forEach((step, index) => {
        step.classList.remove('active', 'completed');
        if (index < currentStep) {
            step.classList.add('completed');
        } else if (index === currentStep) {
            step.classList.add('active');
        }
    });
};

DocumentAccelerationApp.prototype.closePDFProgress = function(progressModal) {
    if (progressModal && progressModal.parentElement) {
        document.body.removeChild(progressModal);
    }
};

// PDF keyboard shortcuts removed - not implemented
DocumentAccelerationApp.prototype.setupPDFKeyboardShortcuts = function() {
    // PDF export not implemented
};

// Initialize keyboard shortcuts when app loads
window.addEventListener('DOMContentLoaded', () => {
    if (window.app && window.app.setupPDFKeyboardShortcuts) {
        window.app.setupPDFKeyboardShortcuts();
    }
});

} // End of extendDocumentAccelerationApp function